import { Tooltip } from "antd";
import type { FC, ReactNode } from "react";
import { PERFORATION_WIDTH, TESTING_WIDTH } from "../../base";
import type { GeoChartMeta } from "../../types";
import type { PerforationWithLevel } from "./GeoChartBodyPicture";

const getEvenRange = (start: number, end: number): number[] => {
	const result = [start];

	for (let i = Math.ceil(start); i < end; i++) {
		if (i % 2 === 0) {
			result.push(i);
		}
	}

	result.push(end);
	return result;
};

const getLines = (start: number, end: number, x: number): ReactNode[] => {
	const clipPathId = `clip-${start}-${end}-${x}`;

	return [
		<clipPath id={clipPathId} key={`clip-${clipPathId}`}>
			<rect x={x - 12} y={start} width={12} height={end - start} />
		</clipPath>,

		<rect
			key="bg"
			x={x - 12}
			width={12}
			y={start}
			height={end - start}
			stroke="#6e80a1"
			fillOpacity={0}
			strokeWidth={0.7}
		/>,

		<g key="lines" clipPath={`url(#${clipPathId})`}>
			{Array.from({ length: Math.ceil((end - start) / 8) }, (_, idx) => {
				const i = start + idx * 8;
				return [
					<line
						key={`line1-${i}`}
						x1={x - 12}
						x2={x}
						y1={i}
						y2={i + 8}
						stroke="#6e80a1"
						strokeWidth={0.7}
					/>,
					<line
						key={`line2-${i}`}
						x1={x - 12}
						x2={x}
						y1={i + 8}
						y2={i}
						stroke="#6e80a1"
						strokeWidth={0.7}
					/>,
				];
			})}
		</g>,
	];
};

export const PerforationComponent: FC<
	PerforationWithLevel & { x: number; meta: GeoChartMeta }
> = ({
	absBottom,
	absRoof,
	roof,
	bottom,
	dateTime,
	gearTitle,
	result,
	type,
	x,
	meta,
	level,
}) => {
	const { depthToPixelsY } = meta.depths;
	const TESTING_OFFSET =
		level !== 0 ? (level - 1) * PERFORATION_WIDTH + TESTING_WIDTH : 16;
	x += TESTING_OFFSET + 16;

	const y1 = depthToPixelsY(-absRoof);
	const y2 = depthToPixelsY(-absBottom);
	const centerY = depthToPixelsY(-(absRoof + absBottom) / 2);
	const DIFF = (y2 - y1) / 2 < 32 ? 48 - (y2 - y1) / 2 : 16;

	return (
		<>
			{type === "PERFORATION" ? (
				<>
					<line x1={x} x2={x} y1={y1} y2={y2} stroke="currentColor" />
					{getEvenRange(-absRoof, -absBottom).map((y) => (
						<line
							key={`${x - 8}${x}${depthToPixelsY(y)}${depthToPixelsY(y)}`}
							x1={x - 8}
							x2={x}
							y1={depthToPixelsY(y)}
							y2={depthToPixelsY(y)}
							stroke="currentColor"
						/>
					))}
				</>
			) : (
				getLines(y1, y2, x)
			)}
			<Tooltip
				title={
					<>
						<span className="text-xxs">{dateTime}</span>
						<br />
						<span className="text-xxs">{gearTitle}</span>
						<br />
						<span className="text-xxs">{result}</span>
					</>
				}
			>
				<text
					x={x + 16}
					y={centerY}
					fill={"currentColor"}
					fontSize={10}
					textAnchor="middle"
					className="cursor-pointer"
					transform={`rotate(-90, ${x + 16}, ${centerY})`}
				>
					<tspan x={x + 16} dy="0">
						{dateTime}
					</tspan>
					<tspan x={x + 16} dy="1.2em">
						{gearTitle}
					</tspan>
					<tspan x={x + 16} dy="1.2em">
						{result}
					</tspan>
				</text>
			</Tooltip>
			<Tooltip
				title={<span className="text-xxs">{`${roof} (${absRoof})`}</span>}
			>
				<text
					x={x}
					y={depthToPixelsY(-absRoof) - DIFF}
					fill={"currentColor"}
					fontSize={10}
					textAnchor="middle"
					className="cursor-pointer"
				>
					<tspan x={x - 4} dy={0}>
						{Math.round(roof * 100) / 100}
					</tspan>
					<tspan x={x - 4} dy="1.2em">
						({Math.round(absRoof * 100) / 100})
					</tspan>
				</text>
			</Tooltip>
			<Tooltip
				title={<span className="text-xxs">{`${bottom} (${absBottom})`}</span>}
			>
				<text
					x={x}
					y={depthToPixelsY(-absBottom) + DIFF}
					fill={"currentColor"}
					fontSize={10}
					textAnchor="middle"
					className="cursor-pointer"
				>
					<tspan x={x - 4} dy={0}>
						{Math.round(bottom * 100) / 100}
					</tspan>
					<tspan x={x - 4} dy="1.2em">
						({Math.round(absBottom * 100) / 100})
					</tspan>
				</text>
			</Tooltip>
		</>
	);
};
